import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';

import apiService from './api';

import type { Observable } from 'rxjs';

export class TokenService {
  private token$: BehaviorSubject<string>;
  private refreshToken$: BehaviorSubject<string>;
  private accountsSsid$: BehaviorSubject<string>;
  private accountsId$: BehaviorSubject<string>;
  private qualificationKey$: BehaviorSubject<string>;

  constructor() {
    this.token$ = new BehaviorSubject(null);
    this.refreshToken$ = new BehaviorSubject(null);
    this.accountsSsid$ = new BehaviorSubject(null);
    this.accountsId$ = new BehaviorSubject(null);
    this.qualificationKey$ = new BehaviorSubject(null);
  }

  public getToken(): Observable<string> {
    return this.token$.pipe(distinctUntilChanged());
  }

  public getRefreshToken(): Observable<string> {
    return this.refreshToken$.pipe(distinctUntilChanged());
  }

  public setToken(token: string): Observable<string> {
    sessionStorage.setItem('authToken', token);
    this.token$.next(token);
    return this.getToken();
  }

  public getAccountsSsid(): Observable<string> {
    return this.accountsSsid$.pipe(distinctUntilChanged());
  }

  public setAccountsSsid(ssid: string): Observable<string> {
    this.accountsSsid$.next(ssid);

    return this.getAccountsSsid();
  }

  public getAccountsId(): Observable<string> {
    return this.accountsId$.pipe(distinctUntilChanged());
  }

  public setAccountsId(id: string): Observable<string> {
    this.accountsId$.next(id);

    return this.getAccountsId();
  }

  public getQualificationKey(): Observable<string> {
    return this.qualificationKey$.pipe(distinctUntilChanged());
  }

  public setQualificationKey(id: string) {
    this.qualificationKey$.next(id);
  }

  public setRefreshToken(refreshToken: string): Observable<string> {
    this.refreshToken$.next(refreshToken);
    return this.getRefreshToken();
  }

  public clearToken(): Observable<void> {
    sessionStorage.setItem('authToken', null);
    return this.setToken(null).pipe(map(() => null));
  }

  public decode<T>(token: string): T {
    try {
      const data = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      return currentTime > data.exp ? null : data;
    } catch (err) {
      return null;
    }
  }

  public tryRefreshToken() {
    return this.getRefreshToken().pipe(
      take(1),
      switchMap((refreshToken) => apiService.post('/auth/refresh', { refreshToken })),
      switchMap((data) => this.setToken(data.token)),
      map(() => true),
      catchError(() => of(false))
    );
  }
}

const tokenService = new TokenService();
export default tokenService;
